import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, Share2, Settings, UserPlus } from 'lucide-react';
import { useTheme } from './ThemeContext';
import ShareModal from './ShareModal';

interface NavbarProps {
    onLogout?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onLogout }) => {
    const navigate = useNavigate();
    const { theme, setTheme } = useTheme();
    const [showSettings, setShowSettings] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const settingsRef = useRef<HTMLDivElement>(null);

    const toggleTheme = () => {
        setTheme(theme === 'dark' ? 'light' : 'dark');
    };


    const handleSignUp = () => {
        navigate('/signup');
    };

    // Close settings on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
                setShowSettings(false);
            }
        };

        if (showSettings) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSettings]);

    return (
        <>
            <nav className={`w-full px-4 py-3 flex justify-between items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {/* Left: Branding */}
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center shadow-lg shadow-primary-500/30">
                        <span className="text-white font-bold text-sm">V</span>
                    </div>
                    <div className="hidden sm:block">
                        <h1 className="text-sm font-bold tracking-tight">VerdictAI</h1>
                        <p className={`text-[10px] ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Legal Research Engine</p>
                    </div>
                </div>

                {/* Right: Actions */}
                <div className="flex items-center space-x-2">
                    {/* Share Button */}
                    <button
                        onClick={() => setShowShareModal(true)}
                        className={`p-2 rounded-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium ${theme === 'dark'
                            ? 'hover:bg-dark-bg-elevated text-gray-300 hover:text-white'
                            : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                            }`}
                        title="Share/Export Chat"
                    >
                        <Share2 size={18} />
                        <span className="hidden sm:inline">Share</span>
                    </button>

                    {/* Theme Toggle */}
                    <button
                        onClick={toggleTheme}
                        className={`p-2 rounded-lg transition-all duration-200 ${theme === 'dark'
                            ? 'hover:bg-dark-bg-elevated text-yellow-400'
                            : 'hover:bg-gray-100 text-gray-600'
                            }`}
                        title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                    >
                        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                    </button>

                    {/* Settings Dropdown */}
                    <div className="relative" ref={settingsRef}>
                        <button
                            onClick={() => setShowSettings(!showSettings)}
                            className={`p-2 rounded-lg transition-all duration-200 ${theme === 'dark'
                                ? 'hover:bg-dark-bg-elevated text-gray-300'
                                : 'hover:bg-gray-100 text-gray-600'
                                }`}
                        >
                            <Settings size={20} />
                        </button>

                        {showSettings && (
                            <div className={`absolute right-0 mt-2 w-48 rounded-xl shadow-2xl border z-50 overflow-hidden ${theme === 'dark'
                                ? 'bg-dark-bg-elevated border-dark-border'
                                : 'bg-white border-gray-100'
                                }`}>
                                <div className={`px-4 py-3 border-b ${theme === 'dark' ? 'border-dark-border' : 'border-gray-100'}`}>
                                    <p className="text-sm font-medium">User Settings</p>
                                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>contact@verdictai.com</p>
                                </div>

                                <button
                                    onClick={handleSignUp}
                                    className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-opacity-10 ${theme === 'dark' ? 'hover:bg-white text-gray-200' : 'hover:bg-black text-gray-700'
                                        }`}
                                >
                                    <UserPlus size={16} />
                                    <span>Sign Up</span>
                                </button>

                                {/* Sign Out Removed as per request */}
                            </div>
                        )}
                    </div>
                </div>
            </nav>
            <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} />
        </>
    );
};

export default Navbar;